// ============================================================
// frontend/src/views/ClientsView.tsx
// ============================================================
// Clients list with search + create modal.
// ClientForm is exported so it can be re-used by ClientDetailView
// for editing (same fields, just different submit URL).
// ============================================================

import { useEffect, useState } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { api } from '../lib/api';
import { useSaving } from '../lib/useSaving';
import { Modal, ModalFooter, Field } from '../components/UI';
import { SaveButton } from '../components/SaveButton';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type {
  ClientListResponse, ClientListItem, Client,
  CreateClientRequest,
} from '@consilium/shared';
import type { ViewState } from '../App';

interface ClientsViewProps {
  setView: (v: ViewState) => void;
  showError: (msg: string) => void;
}

export function ClientsView({ setView, showError }: ClientsViewProps) {
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { clients } = await api.get<ClientListResponse>('/clients');
        if (!cancelled) setClients(clients);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load clients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter client-side — fine for hundreds of records.
  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  // After creation, prepend to list (no full refetch)
  const handleCreated = (newClient: Client) => {
    setClients([{
      id: newClient.id,
      name: newClient.name,
      email: newClient.email,
      phone: newClient.phone,
      dob: newClient.dob,
      presenting: newClient.presenting,
      status: newClient.status,
      createdAt: newClient.createdAt,
      activePackageName: null,
      activePackageIsOneOff: null,
      upcomingSessionCount: 0,
    }, ...clients]);
    setShowAdd(false);
  };

  return (
    <div className="fade-in">
      <div className="row" style={{ alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div className="mono-cap">Caseload</div>
          <h1 className="page-title">Clients</h1>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>
          <Plus size={16} />New client
        </button>
      </div>

      <input
        className="input"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 20, maxWidth: 400 }}
      />

      {loading ? (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><ConsiliumLoader size={48} /></div>
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <div className="display">{search ? 'No matches' : 'No clients yet'}</div>
          <div style={{ fontSize: 14 }}>
            {search ? 'Try a different search term.' : 'Add your first client to get started.'}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.map((c) => {
            const subtitle = c.activePackageName
              ?? (c.activePackageIsOneOff ? 'On one-off basis' : 'No active package');
            const upcomingText = c.upcomingSessionCount > 0
              ? ` · ${c.upcomingSessionCount} upcoming`
              : '';
            return (
              <div
                key={c.id} className="list-row"
                onClick={() => setView({ name: 'client', clientId: c.id })}
              >
                <div className="row-main">
                  <div className="row-primary">{c.name}</div>
                  <div className="row-secondary">{subtitle}{upcomingText}</div>
                </div>
                <ChevronRight size={18} color="var(--muted-strong)" />
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <ClientForm
          onCreated={handleCreated}
          onCancel={() => setShowAdd(false)}
          showError={showError}
        />
      )}
    </div>
  );
}

// ============================================================
// ClientForm — used for both create and edit
// ============================================================
interface ClientFormProps {
  initial?: Client;
  onCreated?: (c: Client) => void;
  onUpdated?: (c: Client) => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}

export function ClientForm({ initial, onCreated, onUpdated, onCancel, showError }: ClientFormProps) {
  const isEdit = !!initial;
  const [data, setData] = useState<CreateClientRequest>(initial
    ? {
        name: initial.name, email: initial.email, phone: initial.phone,
        dob: initial.dob, presenting: initial.presenting, background: initial.background,
      }
    : { name: '', email: '', phone: '', dob: '', presenting: '', background: '' }
  );
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!data.name.trim()) return;
    try {
      const result = await run(async () => {
        if (isEdit) {
          const r = await api.put<{ client: Client }>(`/clients/${initial!.id}`, data);
          return r.client;
        } else {
          const r = await api.post<{ client: Client }>('/clients', data);
          return r.client;
        }
      });
      if (!result) return;
      // Delay close briefly so the "Saved" indicator is visible.
      setTimeout(() => {
        if (isEdit) onUpdated?.(result);
        else onCreated?.(result);
      }, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <Modal title={isEdit ? 'Edit client' : 'New client'} onClose={onCancel}>
      <div className="stack-lg">
        <Field label="Full name" required>
          <input
            className="input" value={data.name} autoFocus
            onChange={(e) => setData({ ...data, name: e.target.value })}
          />
        </Field>
        <div className="grid-2">
          <Field label="Email">
            <input
              className="input" value={data.email || ''}
              onChange={(e) => setData({ ...data, email: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <input
              className="input" value={data.phone || ''}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Date of birth">
          <input
            type="date" className="input" value={data.dob || ''}
            onChange={(e) => setData({ ...data, dob: e.target.value })}
          />
        </Field>
        <Field label="Presenting concern">
          <textarea
            className="input" value={data.presenting || ''}
            placeholder="What brought them in?"
            onChange={(e) => setData({ ...data, presenting: e.target.value })}
          />
        </Field>
        <Field label="Background / intake notes">
          <textarea
            className="input" value={data.background || ''}
            placeholder="History, context, relevant background…"
            onChange={(e) => setData({ ...data, background: e.target.value })}
          />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={!data.name.trim()}
        >
          {isEdit ? 'Save changes' : 'Add client'}
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}
